import { useState, useCallback, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';

const HANDLE_PATTERN = /^[a-z0-9][a-z0-9_]{1,18}[a-z0-9]$|^[a-z0-9]{3}$/;

const RESERVED_HANDLES = new Set([
  'song', 'user', 'settings', 'notifs', 'search', 'explore', 'feed', 'home',
  'admin', 'login', 'logout', 'signup', 'register', 'verify', 'reset',
  'help', 'support', 'about', 'contact', 'privacy', 'terms', 'legal',
  'goodsong', 'goodsongclub', 'gsc',
  'me', 'you', 'god', 'null', 'undefined', 'anonymous',
]);

const ERROR_MESSAGES = {
  too_short: 'Handle must be at least 3 characters.',
  too_long: 'Handle must be 20 characters or fewer.',
  invalid_chars: 'Only letters, numbers, and underscores are allowed.',
  invalid_underscore: 'Handle cannot start or end with an underscore.',
  invalid_format: 'Invalid handle format.',
  reserved: 'That handle is reserved.',
  taken: 'That handle is already taken.',
  server_error: 'Something went wrong. Please try again.',
};

/**
 * Validates handle format client-side. Returns an error key or null if valid.
 */
export function validateHandle(raw) {
  if (!raw || typeof raw !== 'string') return 'too_short';
  const handle = raw.toLowerCase().trim();
  if (handle.length < 3) return 'too_short';
  if (handle.length > 20) return 'too_long';
  if (!/^[a-z0-9_]+$/.test(handle)) return 'invalid_chars';
  if (handle.startsWith('_') || handle.endsWith('_')) return 'invalid_underscore';
  if (!HANDLE_PATTERN.test(handle)) return 'invalid_format';
  if (RESERVED_HANDLES.has(handle)) return 'reserved';
  return null;
}

export function getHandleErrorMessage(errorKey) {
  return ERROR_MESSAGES[errorKey] || ERROR_MESSAGES.invalid_format;
}

/**
 * Hook for handle claim flow.
 * Provides debounced availability checking and handle submission.
 */
const useHandle = () => {
  const [checking, setChecking] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [availability, setAvailability] = useState(null); // null | { available, reason }
  const debounceRef = useRef(null);

  const checkAvailability = useCallback((rawHandle) => {
    // Clear any pending check
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const formatError = validateHandle(rawHandle);
    if (formatError) {
      setAvailability({ available: false, reason: formatError });
      return;
    }

    setAvailability(null);
    setChecking(true);

    debounceRef.current = setTimeout(async () => {
      try {
        const fn = httpsCallable(functions, 'checkHandleAvailability');
        const result = await fn({ handle: rawHandle.trim() });
        setAvailability(result.data);
      } catch {
        setAvailability({ available: false, reason: 'server_error' });
      } finally {
        setChecking(false);
      }
    }, 400);
  }, []);

  const claimHandle = useCallback(async (rawHandle) => {
    setClaiming(true);
    try {
      const fn = httpsCallable(functions, 'claimHandle');
      const result = await fn({ handle: rawHandle.trim() });
      return result.data; // { success, handle } or { success: false, error }
    } catch {
      return { success: false, error: 'server_error' };
    } finally {
      setClaiming(false);
    }
  }, []);

  return {
    checking,
    claiming,
    availability,
    checkAvailability,
    claimHandle,
    getHandleErrorMessage,
  };
};

export default useHandle;
